import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { Subscription } from 'rxjs';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';

import { ViMonitorService } from '../../services/vi-monitor.service';

@Component({
    selector: 'agpr-vi-active-question-summary',
    templateUrl: './vi-active-question-summary.component.html',
    styleUrls: ['./vi-active-question-summary.component.scss'],
    standalone: false,
    animations: [
        trigger('cardState', [
            state('collapsed', style({
                height: '0'
            })),
            state('expanded', style({
                height: '*'
            })),
            transition('collapsed <=> expanded', animate('220ms ease-in'))
        ])
    ]
})
export class ViActiveQuestionSummaryComponent implements OnInit, OnChanges, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly quotaWarningThreshold: number = 0.75;
    readonly contactLink: string = 'https://www.agilitypr.com/contact/';

    @Input() id: string = 'viActiveQuestionSummary';
    @Input() questionsCount: number = 0;

    isExpanded: boolean = false;
    totalActiveQuestions: number = 0;
    totalActiveQuestionsUsed: number = 0;
    remainingActiveQuestions: number = 0;

    bannerSeverity: messageSeverityEnum = messageSeverityEnum.info;
    bannerMessage: string = '';

    private subscriptions: Subscription[] = [];

    constructor(
        private localiztionService: LocalizationService,
        private viMonitorService: ViMonitorService
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.viMonitorService.getMonitorQuestionLimit().subscribe(limit => {
                this.totalActiveQuestions = limit;
                this.refresh();
            }),
            this.viMonitorService.totalQuestionsUsed$.subscribe(value => {
                this.totalActiveQuestionsUsed = value;
                this.refresh();
            })
        );
    }

    ngOnChanges(): void {
        this.refresh();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    toggle(): void {
        this.isExpanded = !this.isExpanded;
    }

    private refresh(): void {
        this.remainingActiveQuestions = this.totalActiveQuestions - this.totalActiveQuestionsUsed;

        const usageRatio = this.totalActiveQuestions > 0 ? (this.totalActiveQuestionsUsed + this.questionsCount) / this.totalActiveQuestions : Infinity;
        if (usageRatio >= 1) {
            this.bannerSeverity = messageSeverityEnum.error;
            this.bannerMessage = this.localiztionService.transform(
                'VisibilityIntelligence.querySetup.activeQuestionSummary.banner.error',
                {remaining: this.remainingActiveQuestions, contactLink: this.contactLink}
            );
        } else if (usageRatio >= this.quotaWarningThreshold) {
            this.bannerSeverity = messageSeverityEnum.warning;
            this.bannerMessage = this.localiztionService.transform(
                'VisibilityIntelligence.querySetup.activeQuestionSummary.banner.warning',
                {remaining: this.remainingActiveQuestions, contactLink: this.contactLink}
            );
        } else {
            this.bannerSeverity = messageSeverityEnum.info;
            this.bannerMessage = this.localiztionService.transform(
                'VisibilityIntelligence.querySetup.activeQuestionSummary.banner.info',
                {remaining: this.remainingActiveQuestions, contactLink: this.contactLink}
            );
        }
    }
}
